import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, CalendarDays, MapPin, IndianRupee, Clock, Stethoscope, Users, ArrowRight, Bookmark, Trash2, Repeat } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/lib/config";
import { track } from "@/lib/track";
import { SHIFT_STATUS_STYLES } from "@/lib/utils-lc";

const SPECIALTIES = ["General Physician", "Anaesthesia", "Paediatrics", "Emergency", "Cardiology", "Orthopaedics", "OBG", "Radiology", "ICU / Critical Care", "Nurse — General Ward", "Nurse — ICU", "Nurse — OT", "Clinical Assistant", "Alternative Medicine"];

export default function PeerCovers({ verified }) {
  const [covers, setCovers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null); // template values to pre-populate the form
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([api.get("/covers/mine"), api.get("/cover-templates/mine")]);
      setCovers(c.data);
      setTemplates(t.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { if (verified) load(); }, [verified]);

  const applyTemplate = (tpl) => {
    // Pre-fill the modal with the template values (date left blank for the user to fill).
    setPrefill({
      hospital_name: tpl.hospital_name,
      specialty: tpl.specialty,
      start_time: tpl.start_time,
      end_time: tpl.end_time,
      pay_inr: String(tpl.pay_inr),
      location: tpl.location,
      city: tpl.city || "",
      notes: tpl.notes || "",
      payer_role: tpl.payer_role,
      hospital_contact_name: tpl.hospital_contact_name || "",
      hospital_contact_phone: tpl.hospital_contact_phone || "",
      date: "",
    });
    setOpen(true);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/cover-templates/${id}`);
      toast.success("Template deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (!verified) {
    return <Card className="rounded-2xl border-slate-200 p-8 text-center bg-white text-slate-500">Posting a cover request opens up once your credentials are verified.</Card>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Peer cover requests</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">Need someone to cover your shift?</h2>
          <p className="text-slate-600 mt-1 text-sm">Post it here — verified peers will see it under Browse. You choose who pays.</p>
        </div>
        <Button data-testid="open-post-cover-btn" onClick={() => { setPrefill(null); setOpen(true); }} className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-11 px-5">
          <Plus className="w-4 h-4 mr-1.5" /> Post a cover
        </Button>
      </div>

      {templates.length > 0 && (
        <Card className="rounded-2xl border-slate-200 p-5 bg-white">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-slate-500 mb-3"><Bookmark className="w-3.5 h-3.5" /> Saved templates</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {templates.map(t => (
              <div key={t.template_id} className="border border-slate-200 rounded-xl p-3 bg-slate-50/40" data-testid={`template-${t.template_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{t.label}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{t.specialty} · {t.start_time}–{t.end_time} · ₹{Number(t.pay_inr).toLocaleString("en-IN")}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{t.hospital_name} · {t.city || "—"}</div>
                  </div>
                  <button onClick={() => deleteTemplate(t.template_id)} className="p-1.5 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-700" data-testid={`delete-template-${t.template_id}`} aria-label="Delete template">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Button size="sm" onClick={() => applyTemplate(t)} className="mt-3 rounded-full bg-emerald-700 hover:bg-emerald-800 h-8 text-xs" data-testid={`use-template-${t.template_id}`}>
                  <Repeat className="w-3 h-3 mr-1" /> Post now
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading ? <div className="text-sm text-slate-500">Loading…</div>
      : covers.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-slate-300 p-10 text-center bg-white">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-800 flex items-center justify-center mx-auto"><CalendarDays className="w-5 h-5" /></div>
          <div className="font-display text-lg font-semibold mt-4">No cover requests yet</div>
          <p className="text-sm text-slate-500 mt-1">Your posted covers will appear here.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {covers.map(c => <CoverCard key={c.shift_id} c={c} />)}
        </div>
      )}

      {open && <PostCoverModal prefill={prefill} onClose={() => { setOpen(false); setPrefill(null); }} onCreated={async () => { setOpen(false); setPrefill(null); await load(); }} />}
    </div>
  );
}

function CoverCard({ c }) {
  const cls = SHIFT_STATUS_STYLES[c.status] || "bg-slate-100 text-slate-700";
  return (
    <Card className="rounded-2xl border-slate-200 p-5 bg-white" data-testid={`cover-card-${c.shift_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
            <span className="text-emerald-800"><Stethoscope className="w-3 h-3 inline mr-0.5" /> {c.specialty}</span>
            <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 text-[10px]">Peer cover</Badge>
          </div>
          <div className="font-display text-lg font-semibold mt-1">{c.facility_name}</div>
          <div className="text-sm text-slate-600 mt-0.5"><Clock className="w-3.5 h-3.5 inline mb-0.5 mr-0.5" />{c.date} · {c.start_time}–{c.end_time}</div>
          <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.location}{c.city ? ` · ${c.city}` : ""}</div>
        </div>
        <Badge className={cls + " hover:" + cls}>{c.status}</Badge>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
        <span className="text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.applicant_count || 0} applicant{c.applicant_count === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1 font-display font-bold text-slate-900"><IndianRupee className="w-3.5 h-3.5" />{Number(c.pay_inr).toLocaleString("en-IN")}</span>
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.15em] text-slate-500">
        Payer: {c.payer_role === "self" ? "You" : c.payer_role === "hospital" ? `Hospital (${c.hospital_contact_name || "contact set"})` : "Cash between peers"}
      </div>
    </Card>
  );
}

function PostCoverModal({ prefill, onClose, onCreated }) {
  const { user } = useAuth();
  const cfg = useConfig();
  const [f, setF] = useState(prefill ? {
    ...prefill,
    city: prefill.city || user?.profile?.city || cfg.supported_cities?.[0] || "Pune",
  } : {
    hospital_name: "",
    specialty: user?.profile?.specialty || "",
    date: "", start_time: "", end_time: "", pay_inr: "",
    location: "", city: user?.profile?.city || cfg.supported_cities?.[0] || "Pune",
    notes: "",
    payer_role: "self",
    hospital_contact_name: "", hospital_contact_phone: "",
  });
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateLabel, setTemplateLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.hospital_name || !f.specialty || !f.date || !f.start_time || !f.end_time || !f.pay_inr || !f.location) {
      return toast.error("Fill all required fields");
    }
    if (f.payer_role === "hospital" && !(f.hospital_contact_name && f.hospital_contact_phone)) {
      return toast.error("Provide the hospital contact name + phone");
    }
    if (saveTemplate && !templateLabel.trim()) {
      return toast.error("Give the template a short name");
    }
    setBusy(true);
    try {
      const payload = { ...f, pay_inr: Number(f.pay_inr) };
      if (!f.hospital_contact_phone.startsWith("+91") && f.hospital_contact_phone) {
        payload.hospital_contact_phone = `+91${f.hospital_contact_phone}`;
      }
      await api.post("/covers", payload);
      track("cover_posted", { city: f.city, specialty: f.specialty, payer: f.payer_role });
      if (saveTemplate) {
        const { date: _drop, ...tplBody } = payload;
        await api.post("/cover-templates", { ...tplBody, label: templateLabel.trim() });
        track("cover_template_saved", { specialty: f.specialty });
      }
      toast.success(saveTemplate ? "Cover posted + template saved" : "Cover request posted — verified peers will see it in Browse");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Peer cover</div>
            <div className="font-display text-lg font-bold">Post a cover request</div>
          </div>
          <button onClick={onClose} data-testid="close-post-cover" className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <Field label="Hospital / clinic where the shift is *"><Input data-testid="cover-hospital-name" value={f.hospital_name} onChange={e => setF({...f, hospital_name: e.target.value})} placeholder="e.g. Ruby Hall Clinic" className="h-11 rounded-xl" /></Field>
          <Field label="Specialty *">
            <Select value={f.specialty} onValueChange={(v) => setF({...f, specialty: v})}>
              <SelectTrigger data-testid="cover-specialty" className="h-11 rounded-xl"><SelectValue placeholder="Choose specialty" /></SelectTrigger>
              <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="City *">
            <Select value={f.city} onValueChange={(v) => setF({...f, city: v})}>
              <SelectTrigger data-testid="cover-city" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{cfg.supported_cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date *"><Input data-testid="cover-date" type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} className="h-11 rounded-xl" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start *"><Input data-testid="cover-start" type="time" value={f.start_time} onChange={e => setF({...f, start_time: e.target.value})} className="h-11 rounded-xl" /></Field>
            <Field label="End *"><Input data-testid="cover-end" type="time" value={f.end_time} onChange={e => setF({...f, end_time: e.target.value})} className="h-11 rounded-xl" /></Field>
          </div>
          <Field label="Pay (₹) *"><Input data-testid="cover-pay" type="number" value={f.pay_inr} onChange={e => setF({...f, pay_inr: e.target.value})} className="h-11 rounded-xl" /></Field>
          <Field label="Location (address) *"><Input data-testid="cover-location" value={f.location} onChange={e => setF({...f, location: e.target.value})} className="h-11 rounded-xl" /></Field>
          <Field label="Notes (optional)"><Textarea data-testid="cover-notes" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} rows={2} className="rounded-xl" /></Field>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 block">Who pays the cover?</Label>
            <RadioGroup value={f.payer_role} onValueChange={(v) => setF({...f, payer_role: v})} className="space-y-2">
              <PayerOpt id="pay-self" testid="cover-payer-self" value="self" title="I'll pay from my account" desc="Escrow / UPI held from your side. 10% platform fee." />
              <PayerOpt id="pay-hospital" testid="cover-payer-hospital" value="hospital" title="My hospital will pay" desc="Provide HR contact — payment flows from the hospital." />
              <PayerOpt id="pay-cash" testid="cover-payer-cash" value="cash" title="Cash between us" desc="Peer handshake. Platform fee billed separately later." />
            </RadioGroup>
          </div>

          {f.payer_role === "hospital" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Contact name *"><Input data-testid="cover-hospital-contact-name" value={f.hospital_contact_name} onChange={e => setF({...f, hospital_contact_name: e.target.value})} className="h-11 rounded-xl" placeholder="HR / roster in-charge" /></Field>
              <Field label="Contact phone *"><Input data-testid="cover-hospital-contact-phone" value={f.hospital_contact_phone} onChange={e => setF({...f, hospital_contact_phone: e.target.value})} className="h-11 rounded-xl" placeholder="98XXXXXXXX" /></Field>
            </div>
          )}

          <Card className="p-4 rounded-xl border-slate-200 bg-emerald-50/30 border-emerald-200">
            <div className="flex items-start gap-3">
              <Checkbox id="save-tpl" data-testid="cover-save-template" checked={saveTemplate} onCheckedChange={setSaveTemplate} className="mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="save-tpl" className="text-sm text-slate-700 font-medium cursor-pointer">Save as template</Label>
                <div className="text-xs text-slate-500 mt-0.5">Repost the same shift with one tap later.</div>
                {saveTemplate && (
                  <Input data-testid="cover-template-label" value={templateLabel} onChange={e => setTemplateLabel(e.target.value)} placeholder="e.g. Tuesday night ICU" className="mt-2 h-10 rounded-lg" />
                )}
              </div>
            </div>
          </Card>

          <Button type="submit" data-testid="cover-submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800">
            {busy ? "Posting…" : "Post cover request"}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function PayerOpt({ id, testid, value, title, desc }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-emerald-500 has-[:checked]:border-emerald-500 has-[:checked]:bg-emerald-50/40">
      <RadioGroupItem id={id} value={value} className="mt-0.5" data-testid={testid} />
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
